import {
  formValueSchema,
  type Annotation,
  type ContentBlock,
  type FormBlockValue,
  type TextContentBlock,
  type ToolDisplay,
} from './agentProtocol';
import { isShowFormArtifact } from './clientSideTools';
import { ToolNames } from './tools';
import type { RagSources, ToolCallType } from '../entities/ChatMessage';
import { ContentType, ToolType } from '../enums';

/**
 * Everything a block reader needs from a persisted message. Structural
 * rather than the entity so DTOs, trpc projections, and widget-side
 * message items all satisfy it.
 */
export interface BlockSourceRow {
  blocks?: ContentBlock[] | null;
  content?: string | null;
  toolCalls?: ToolCallType[] | null;
}

export interface ToolPresentation {
  display: ToolDisplay;
  kind: string;
}

/** The two synthetic v1 tools that carried the turn's answer in `args`. */
const LEGACY_ANSWER_TOOLS: ReadonlySet<string> = new Set([
  ToolNames.RagResult,
  ToolNames.RagResultOperator,
]);

/**
 * v1 tools whose result IS content: they render in the answer flow rather
 * than being hoisted into the activity trail. The two `rag_result` entries
 * were never real tools — they carried the final answer (and its sources,
 * or the operator handoff card) inside `args`, and their v1 renderers
 * still draw exactly that.
 */
const LEGACY_CARD_PRESENTATION: Readonly<Record<string, ToolPresentation>> = {
  [ToolNames.RagResult]: { display: 'card', kind: 'rag_answer' },
  [ToolNames.RagResultOperator]: { display: 'card', kind: 'operator_answer' },
  [ToolNames.RecommendShopProducts]: {
    display: 'card',
    kind: 'product_cards',
  },
  [ToolNames.ShowShopProductDetail]: {
    display: 'card',
    kind: 'product_detail',
  },
  [ToolNames.GetOperator]: { display: 'card', kind: 'operator' },
  [ToolNames.UsageLimitsError]: { display: 'card', kind: 'usage_limits' },
};

/**
 * Read-side presentation for legacy rows and tool results that predate
 * wire-stamped `display` / `kind`. Keeps the widget free of name-based
 * sniffing.
 */
export function inferToolPresentation(toolName: string): ToolPresentation {
  return (
    LEGACY_CARD_PRESENTATION[toolName] ?? {
      display: 'activity',
      kind: 'builtin',
    }
  );
}

/**
 * Blocks projected from a v1 row (`blocks IS NULL`). Legacy rows carry a
 * single answer string plus a flat tool-call array; the projection turns
 * each tool call into its `tool_call` (+ `tool_result`) pair and appends
 * the answer as the turn's last text block, matching the ordering rule
 * every block consumer relies on.
 *
 * Order is the stored array order — that is what v1 rendered, and the
 * `index` field only ever numbered the tool entries, so sorting by it put
 * every text part in front of every card.
 */
export function blocksFromLegacy(row: BlockSourceRow): ContentBlock[] {
  const blocks: ContentBlock[] = [];

  for (const call of row.toolCalls ?? []) {
    if (call.type === ToolType.Text) {
      if (call.content) {
        blocks.push({ type: 'text', id: call.id, text: call.content });
      }

      continue;
    }

    const presentation = inferToolPresentation(call.name);
    // Legacy answer tools stored their final response in args, not a tool result.
    const answer = LEGACY_ANSWER_TOOLS.has(call.name)
      ? (call.args as { content?: unknown } | undefined)?.content
      : undefined;
    const legacyAnswer =
      typeof answer === 'string' && answer.trim() ? answer : undefined;

    blocks.push({
      type: 'tool_call',
      id: call.id,
      name: call.name,
      args: (call.args ?? {}) as Record<string, unknown>,
    });

    /**
     * A call still awaiting the user has no result — projecting one would
     * make an unanswered form look settled. The form's artifact rides in
     * the same `non_standard` block a v2 row carries, so pending forms
     * have exactly one shape regardless of which generation wrote them.
     */
    if (call.status === 'pending' && !legacyAnswer) {
      if (isShowFormArtifact(call.artifact)) {
        blocks.push({
          type: 'non_standard',
          value: {
            kind: 'form',
            toolCallId: call.id,
            name: call.name,
            artifact: call.artifact,
          },
        });
      }

      continue;
    }

    if (
      call.content !== undefined ||
      call.artifact !== undefined ||
      legacyAnswer !== undefined
    ) {
      blocks.push({
        type: 'tool_result',
        tool_call_id: call.id,
        name: call.name,
        content:
          typeof call.content === 'string' && call.content
            ? call.content
            : (legacyAnswer ?? ''),
        artifact: call.artifact,
        is_error: call.status === 'error',
        display: presentation.display,
        kind: presentation.kind,
      });
    }
  }

  if (row.content) {
    blocks.push({ type: 'text', text: row.content });
  }

  return blocks;
}

/**
 * The one read boundary for message content. Every renderer — widget and
 * admin alike — goes through this and only ever sees blocks.
 */
export function blocksOf(row: BlockSourceRow): ContentBlock[] {
  return row.blocks ?? blocksFromLegacy(row);
}

/**
 * Every tool-call id this row knows about, in block order. Reads through
 * {@link blocksOf}, so it answers the same for a v1 row and a v2 one —
 * which is the point: `toolCalls` is no longer written, and anything
 * asking "did this row already show that call?" must not read it.
 */
export function toolCallIdsOf(row: BlockSourceRow): string[] {
  const ids = new Set<string>();

  for (const block of blocksOf(row)) {
    if (block.type === 'tool_call' && block.id) {
      ids.add(block.id);
      continue;
    }

    if (block.type === 'tool_result') {
      ids.add(block.tool_call_id);
      continue;
    }

    if (block.type === 'non_standard') {
      const toolCallId = (block.value as { toolCallId?: unknown }).toolCallId;

      if (typeof toolCallId === 'string' && toolCallId) {
        ids.add(toolCallId);
      }
    }
  }

  return [...ids];
}

/**
 * A client-side form this row is still waiting on: a `non_standard` form
 * block with no `tool_result` answering it. Both generations project
 * pending forms to that shape, and the widget's live assembler emits it
 * too, so the composer gate, the renderer, and the resume path all agree.
 */
export function pendingFormOf(row: BlockSourceRow): FormBlockValue | null {
  const blocks = blocksOf(row);
  const answered = new Set(
    blocks.flatMap(block =>
      block.type === 'tool_result' ? [block.tool_call_id] : [],
    ),
  );

  for (let index = blocks.length - 1; index >= 0; index -= 1) {
    const block = blocks[index]!;

    if (block.type !== 'non_standard') {
      continue;
    }

    const parsed = formValueSchema.safeParse(block.value);

    if (parsed.success && !answered.has(parsed.data.toolCallId)) {
      return parsed.data;
    }
  }

  return null;
}

/**
 * The turn's answer as a plain string, for the few consumers that want
 * text rather than a projection (omnichannel, exports, thread previews).
 * The last text block is the answer; a turn ending in a tool result has
 * none.
 */
export function textOf(row: BlockSourceRow): string {
  if (!row.blocks) {
    return row.content || legacyRagAnswer(row) || '';
  }

  return answerBlockOf(row.blocks)?.text ?? '';
}

/**
 * The answer of a v1 row that never populated `content`: it lived inside
 * the synthetic `rag_result` call's arguments. The block projection keeps
 * it there so `RagResultTool` can draw the bubble and its source pills
 * together, which means the text consumers need their own way in.
 */
function legacyRagAnswer(row: BlockSourceRow): string | undefined {
  for (const call of row.toolCalls ?? []) {
    if (call.type !== ToolType.Tool || !LEGACY_ANSWER_TOOLS.has(call.name)) {
      continue;
    }

    const content = (call.args as { content?: unknown } | undefined)?.content;

    if (typeof content === 'string' && content) {
      return content;
    }
  }

  return undefined;
}

/**
 * The turn's answer blocks, resolved structurally: a text block is
 * narration when tool activity follows it, so the answer is every text block
 * after the last tool call or result. Providers split one answer across several text
 * blocks (Anthropic does it around thinking and citations), so taking only
 * the last would silently drop half the answer.
 */
export function answerBlocksOf(blocks: ContentBlock[]): TextContentBlock[] {
  const lastToolActivity = blocks.findLastIndex(
    block =>
      block.type === 'tool_call' ||
      block.type === 'tool_call_chunk' ||
      block.type === 'tool_result',
  );

  return blocks.filter(
    (block, index): block is TextContentBlock =>
      block.type === 'text' && index > lastToolActivity,
  );
}

/**
 * The turn's answer as a single text block — the answer blocks joined in
 * order, with annotation offsets rebased onto the joined text. A turn that
 * ends on a tool result has no answer, and says so by returning undefined.
 *
 * Not load-bearing for layout: the renderer partitions by block type. This
 * is for `answered`, exports, omnichannel, and the eval harness.
 */
export function answerBlockOf(
  blocks: ContentBlock[],
): TextContentBlock | undefined {
  const answerBlocks = answerBlocksOf(blocks);
  const first = answerBlocks[0];

  if (!first) {
    return undefined;
  }

  if (answerBlocks.length === 1) {
    return first;
  }

  let offset = 0;
  const annotations: Annotation[] = [];

  for (const block of answerBlocks) {
    for (const annotation of block.annotations ?? []) {
      annotations.push(rebaseAnnotation(annotation, offset));
    }

    offset += block.text.length;
  }

  return {
    ...first,
    text: answerBlocks.map(block => block.text).join(''),
    ...(annotations.length > 0 ? { annotations } : {}),
  };
}

/** Shifts a citation annotation's character offsets by `offset`. */
function rebaseAnnotation(annotation: Annotation, offset: number): Annotation {
  if (annotation.type !== 'citation' || offset === 0) {
    return annotation;
  }

  return {
    ...annotation,
    ...(annotation.start_index === undefined
      ? {}
      : { start_index: annotation.start_index + offset }),
    ...(annotation.end_index === undefined
      ? {}
      : { end_index: annotation.end_index + offset }),
  };
}

/**
 * Client-side fallback for `message.sources`, derived from the blocks'
 * own citation annotations. Live streams resolve annotations onto text
 * blocks but have no `sources` array yet (that is server-resolved after
 * persistence) — this reconstructs an equivalent list so source pills can
 * render before a reload. Callers should prefer `message.sources` and only
 * fall back to this when it is absent or empty.
 *
 * De-dupes by `url` (first occurrence wins, first-citation order
 * preserved) and unions every annotation id that resolves to the same
 * source into `ids`, mirroring `resolveCitedSources` — that is what lets
 * the widget map each inline `[cN]` marker to the right pill.
 */
/**
 * The annotation's `source_type` narrowed back to a `ContentType`. The wire
 * keeps it a plain string so the protocol contract stays free of the entity
 * enum; anything unrecognised falls back to the web shape, which is what a
 * URL-bearing citation looks like when the server did not say otherwise.
 */
function contentTypeOf(sourceType: string | undefined): ContentType {
  const known = Object.values(ContentType) as string[];

  return sourceType && known.includes(sourceType)
    ? (sourceType as ContentType)
    : ContentType.Website;
}

export function sourcesFromAnnotations(blocks: ContentBlock[]): RagSources {
  const byUrl = new Map<string, NonNullable<RagSources>[number]>();
  const order: string[] = [];

  for (const block of blocks) {
    if (block.type !== 'text') {
      continue;
    }

    for (const annotation of block.annotations ?? []) {
      if (annotation.type !== 'citation' || !annotation.url) {
        continue;
      }

      const existing = byUrl.get(annotation.url);

      if (existing) {
        if (annotation.id && !existing.ids?.includes(annotation.id)) {
          existing.ids = [...(existing.ids ?? []), annotation.id];
        }

        continue;
      }

      byUrl.set(annotation.url, {
        type: contentTypeOf(annotation.source_type),
        fileName: annotation.title ?? '',
        url: annotation.url,
        page: '',
        ...(annotation.id ? { id: annotation.id, ids: [annotation.id] } : {}),
      });
      order.push(annotation.url);
    }
  }

  return order.map(url => byUrl.get(url)!);
}
